import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Loader2, Save, Search, Globe, BarChart3, Image as ImageIcon, MessageSquare, Bell } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import ResourceManager from "./ResourceManager";

export default function SEOSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast({ title: "Settings saved", description: "Refresh the site to see changes." });
    } catch (err) {
      toast({ title: "Save failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      await api.put("/settings", form); // persist current recipients/mode first
      const r = await api.post("/admin/send-digest");
      const res = r.data?.result || "";
      const msg = res.startsWith("sent")
        ? `Digest sent (${res.split(":")[1]} lead(s) in last 24h).`
        : res === "no_leads"
        ? "No leads in the last 24 hours — nothing to send."
        : res === "no_recipients"
        ? "Add at least one email address first."
        : res === "alerts_disabled"
        ? "Turn on 'Email me new leads' first."
        : "Done.";
      toast({ title: "Digest", description: msg });
    } catch (err) {
      toast({ title: "Could not send digest", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setSendingDigest(false);
    }
  };

  if (!form) return <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#0B2A6B]">SEO &amp; Settings</h2>
        <p className="text-sm text-slate-500">Global meta tags, analytics, search console verification and per-page SEO.</p>
      </div>

      <Tabs defaultValue="global">
        <TabsList className="mb-6 bg-white border border-slate-200">
          <TabsTrigger value="global"><Search className="h-4 w-4 mr-1.5" />Global SEO</TabsTrigger>
          <TabsTrigger value="analytics"><BarChart3 className="h-4 w-4 mr-1.5" />Analytics &amp; Pixels</TabsTrigger>
          <TabsTrigger value="branding"><ImageIcon className="h-4 w-4 mr-1.5" />Branding &amp; Contact</TabsTrigger>
          <TabsTrigger value="alerts"><Bell className="h-4 w-4 mr-1.5" />Lead Alerts</TabsTrigger>
          <TabsTrigger value="social"><Globe className="h-4 w-4 mr-1.5" />Social Links</TabsTrigger>
          <TabsTrigger value="pages"><MessageSquare className="h-4 w-4 mr-1.5" />Per-Page SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="global">
          <form onSubmit={save} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-5">
            <Field label="Site Title" help="Used as default site name across pages and Open Graph.">
              <Input value={form.site_title || ""} onChange={(e) => update("site_title", e.target.value)} />
            </Field>
            <Field label="Site Tagline">
              <Input value={form.site_tagline || ""} onChange={(e) => update("site_tagline", e.target.value)} />
            </Field>
            <Field label="Default Meta Title" help="Used when a page doesn't have its own SEO entry.">
              <Input value={form.default_meta_title || ""} onChange={(e) => update("default_meta_title", e.target.value)} />
            </Field>
            <Field label="Default Meta Description" help="Recommended 150-160 characters.">
              <Textarea rows={3} value={form.default_meta_description || ""} onChange={(e) => update("default_meta_description", e.target.value)} />
            </Field>
            <Field label="Default Meta Keywords" help="Comma-separated.">
              <Input value={form.default_meta_keywords || ""} onChange={(e) => update("default_meta_keywords", e.target.value)} />
            </Field>
            <Field label="Canonical Base URL" help="Your live domain, e.g. https://kikicentre.com.">
              <Input value={form.canonical_base_url || ""} onChange={(e) => update("canonical_base_url", e.target.value)} />
            </Field>
            <Field label="Default OG Image URL" help="Shown when your site is shared on social media.">
              <Input value={form.og_image || ""} onChange={(e) => update("og_image", e.target.value)} />
            </Field>
            <SaveBar saving={saving} />
          </form>
        </TabsContent>

        <TabsContent value="analytics">
          <form onSubmit={save} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-5">
            <Field label="Google Analytics 4 Measurement ID" help="Format: G-XXXXXXXXXX. Find this in Google Analytics > Admin > Data Streams.">
              <Input value={form.google_analytics_id || ""} onChange={(e) => update("google_analytics_id", e.target.value)} placeholder="G-XXXXXXXXXX" />
            </Field>
            <Field label="Google Search Console Verification Code" help="Just the content value of the meta tag, e.g. abc123xyz...">
              <Input value={form.google_search_console_verification || ""} onChange={(e) => update("google_search_console_verification", e.target.value)} placeholder="verification token" />
            </Field>
            <Field label="Google Tag Manager Container ID" help="Format: GTM-XXXXXX.">
              <Input value={form.google_tag_manager_id || ""} onChange={(e) => update("google_tag_manager_id", e.target.value)} placeholder="GTM-XXXXXX" />
            </Field>
            <Field label="Facebook Pixel ID">
              <Input value={form.facebook_pixel_id || ""} onChange={(e) => update("facebook_pixel_id", e.target.value)} placeholder="123456789012345" />
            </Field>
            <Field label="Custom Head HTML" help="Any additional script/meta tags you want injected into the &lt;head&gt; (advanced).">
              <Textarea rows={6} value={form.custom_head_html || ""} onChange={(e) => update("custom_head_html", e.target.value)} placeholder="<meta ...>" />
            </Field>
            <SaveBar saving={saving} />
          </form>
        </TabsContent>

        <TabsContent value="branding">
          <form onSubmit={save} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-5">
            <Field label="Favicon URL"><Input value={form.favicon_url || ""} onChange={(e) => update("favicon_url", e.target.value)} /></Field>
            <Field label="Contact Email"><Input type="email" value={form.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} /></Field>
            <Field label="Contact Phone"><Input value={form.contact_phone || ""} onChange={(e) => update("contact_phone", e.target.value)} placeholder="+918800288994" /></Field>
            <Field label="WhatsApp Number" help="Country code + number, no spaces or +. e.g. 918800288994"><Input value={form.whatsapp_number || ""} onChange={(e) => update("whatsapp_number", e.target.value)} /></Field>
            <Field label="Address"><Textarea rows={2} value={form.address || ""} onChange={(e) => update("address", e.target.value)} /></Field>
            <SaveBar saving={saving} />
          </form>
        </TabsContent>

        <TabsContent value="alerts">
          <form onSubmit={save} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-5">
            <div className="flex items-start justify-between gap-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div>
                <p className="font-semibold text-slate-800 text-sm">Email me new leads instantly</p>
                <p className="text-xs text-slate-500 mt-1">When ON, every enquiry popup, admission form and contact form submission is emailed to the address(es) below.</p>
              </div>
              <Switch
                checked={!!form.lead_notify_enabled}
                onCheckedChange={(v) => update("lead_notify_enabled", v)}
                data-testid="lead-notify-toggle"
              />
            </div>

            <Field label="Notification Email Address(es)" help="Where lead alerts are sent. Add several emails separated by commas (e.g. admissions@school.com, frontdesk@school.com) to notify the whole team.">
              <Input
                value={form.lead_notify_email || ""}
                onChange={(e) => update("lead_notify_email", e.target.value)}
                placeholder="admissions@yourschool.com, frontdesk@yourschool.com"
                data-testid="lead-notify-email"
              />
            </Field>

            <Field label="Delivery frequency" help="Instant = one email per enquiry. Daily digest = a single summary email once a day.">
              <div className="flex flex-wrap gap-2">
                {[
                  { v: "instant", label: "Instant (per enquiry)" },
                  { v: "daily", label: "Daily digest (once a day)" },
                ].map((o) => (
                  <button
                    type="button"
                    key={o.v}
                    onClick={() => update("lead_notify_mode", o.v)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                      (form.lead_notify_mode || "instant") === o.v
                        ? "bg-yellow-400 border-yellow-400 text-[#0B2A6B]"
                        : "bg-white border-slate-200 text-slate-600 hover:border-yellow-400"
                    }`}
                    data-testid={`lead-mode-${o.v}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </Field>

            {(form.lead_notify_mode || "instant") === "daily" && (
              <Field label="Digest send time (IST)" help="The daily summary is sent around this hour, India time.">
                <select
                  value={form.lead_digest_hour ?? 9}
                  onChange={(e) => update("lead_digest_hour", parseInt(e.target.value, 10))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:border-yellow-400"
                  data-testid="lead-digest-hour"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {`${((h % 12) || 12)}:00 ${h < 12 ? "AM" : "PM"} IST`}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-xs text-slate-600">
              Replies go straight to the enquirer: when a lead includes an email, hitting <b>Reply</b> on the alert emails them directly. Leads are always saved in the Admissions / Contact inboxes even if email is off.
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={sendDigestNow}
                disabled={sendingDigest}
                data-testid="send-digest-now"
              >
                {sendingDigest ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</> : <>Send digest now</>}
              </Button>
              <Button type="submit" disabled={saving} className="bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Changes</>}
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="social">
          <form onSubmit={save} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-5">
            <Field label="Facebook URL"><Input value={form.social_facebook || ""} onChange={(e) => update("social_facebook", e.target.value)} /></Field>
            <Field label="Instagram URL"><Input value={form.social_instagram || ""} onChange={(e) => update("social_instagram", e.target.value)} /></Field>
            <Field label="LinkedIn URL"><Input value={form.social_linkedin || ""} onChange={(e) => update("social_linkedin", e.target.value)} /></Field>
            <Field label="YouTube URL"><Input value={form.social_youtube || ""} onChange={(e) => update("social_youtube", e.target.value)} /></Field>
            <SaveBar saving={saving} />
          </form>
        </TabsContent>

        <TabsContent value="pages">
          <PageSEOTable />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, help, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-700 mb-1.5 inline-block">{label}</Label>
      {children}
      {help && <p className="text-xs text-slate-400 mt-1.5">{help}</p>}
    </div>
  );
}

function SaveBar({ saving }) {
  return (
    <div className="pt-3 flex justify-end">
      <Button type="submit" disabled={saving} className="bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold">
        {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Changes</>}
      </Button>
    </div>
  );
}

function PageSEOTable() {
  return (
    <ResourceManager
      title="Page SEO"
      endpoint="page-seo"
      defaults={{ slug: "", title: "", description: "", keywords: "", og_image: "", canonical: "", no_index: false }}
      fields={[
        { name: "slug", label: "Page Slug", required: true, placeholder: "home / about / courses / contact / ..." },
        { name: "title", label: "Title", required: true, col: "full" },
        { name: "description", label: "Meta Description", type: "textarea", col: "full", rows: 3, required: true },
        { name: "keywords", label: "Keywords (comma-separated)", col: "full" },
        { name: "og_image", label: "OG Image URL", col: "full" },
        { name: "canonical", label: "Canonical URL (optional override)", col: "full" },
        { name: "no_index", label: "Hide from search engines", type: "boolean" },
      ]}
      listColumns={[
        { key: "slug", label: "Slug" },
        { key: "title", label: "Title" },
        { key: "no_index", label: "No-Index" },
      ]}
    />
  );
}
